import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Bot, Workflow, Zap, Layers, Rocket, ArrowRight, CheckCircle2, Sparkles,
  Mail, MessageSquare, Clock, TrendingUp, Shield, Code2,
} from 'lucide-react';
import { useBranding } from '@/contexts/BrandingContext';
import usePageMeta from '@/hooks/usePageMeta';

const SERVICES = [
  {
    icon: Bot, title: 'Robomotion RPA', tagline: 'Our flagship practice',
    points: ['Desktop + browser automation', 'Unattended bots on a cron', 'Scrape, fill, file, reconcile — at scale'],
    hero: true,
  },
  { icon: Workflow, title: 'n8n Workflows', tagline: 'Self-hosted automation',
    points: ['Custom nodes for your stack', 'API chaining + error retries', 'Deploy on your own server'] },
  { icon: Layers, title: 'Make Scenarios', tagline: 'Visual automation',
    points: ['Multi-step branching flows', 'Webhook orchestration', 'Data transformation'] },
  { icon: Zap, title: 'Zapier Integrations', tagline: 'Fast to ship',
    points: ['SaaS-to-SaaS connectors', 'Notification pipelines', 'Team ops automation'] },
  { icon: Code2, title: 'Custom SaaS', tagline: 'FastAPI + React',
    points: ['Multi-tenant, vault-grade security', 'MongoDB, WebSockets, payments', 'Ship production in weeks'] },
  { icon: Rocket, title: 'RPA Consulting', tagline: 'Your automation roadmap',
    points: ['Process discovery & ROI', 'Pilot → scale playbook', 'Ongoing managed ops'] },
];

const USE_CASES = [
  { title: 'Invoice reconciliation', metric: '40h/wk → 2h', detail: 'Bot pulls invoices from 4 sources, matches against POs, flags mismatches.' },
  { title: 'Lead enrichment', metric: '$6/lead → $0.08', detail: 'Scrape LinkedIn + Crunchbase + company site into CRM nightly.' },
  { title: 'Customer onboarding', metric: '3 days → 2 hours', detail: 'KYC → account provisioning → welcome email → Slack invite, fully automated.' },
  { title: 'Report generation', metric: 'Monthly → real-time', detail: 'Pull from 6 databases, chart, narrate with AI, email stakeholders.' },
];

export default function Landing() {
  const branding = useBranding();
  usePageMeta({
    title: `${branding.app_name || 'Krris OS'} — Automate the boring. Reclaim your week.`,
    description: 'Robomotion RPA, n8n workflows, Make, Zapier, and custom SaaS. One operator, multiple brands, production-ready automation.',
  });

  const [typedIdx, setTypedIdx] = useState(0);
  const phrases = [
    'Automate the boring.',
    'Reclaim your week.',
    'Ship faster, with less code.',
    'Turn spreadsheets into bots.',
  ];

  useEffect(() => {
    const t = setInterval(() => setTypedIdx(i => (i + 1) % phrases.length), 3200);
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  const primary = branding.primary_color || '#006080';

  return (
    <div className="min-h-screen" style={{ background: '#FAF8F4', fontFamily: "'Bricolage Grotesque', sans-serif", color: '#1A2428' }}>
      {/* Animated background grid */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.04]" style={{
        backgroundImage: `radial-gradient(${primary} 1px, transparent 1px)`,
        backgroundSize: '28px 28px',
      }} />
      <div className="fixed -top-20 -left-20 w-96 h-96 rounded-full opacity-[0.12] pointer-events-none blur-3xl animate-pulse" style={{ background: primary }} />
      <div className="fixed -bottom-20 -right-20 w-96 h-96 rounded-full opacity-[0.08] pointer-events-none blur-3xl animate-pulse" style={{ background: '#D9A441', animationDelay: '2s' }} />

      {/* Nav */}
      <nav className="relative z-10 px-6 py-5 flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-btn flex items-center justify-center" style={{ background: primary }}>
            <Sparkles size={18} color="white" />
          </div>
          <span className="font-black text-lg">krri.sh</span>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <Link to="/features" className="hidden sm:inline font-semibold hover:opacity-70">{branding.app_name}</Link>
          <a href="#services" className="hidden md:inline opacity-80 hover:opacity-100">Services</a>
          <a href="#contact" className="hidden md:inline opacity-80 hover:opacity-100">Contact</a>
          <Link to="/login" className="text-xs font-semibold px-3 py-1.5 rounded-btn" style={{ background: 'white', border: '1px solid #E8E3D9' }}>Sign in</Link>
          <Link to="/signup" className="text-xs font-bold px-3 py-1.5 rounded-btn text-white" style={{ background: primary }} data-testid="nav-signup">Start a project</Link>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pt-12 pb-24">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-xs uppercase tracking-[0.3em] font-bold mb-6"
          style={{ color: primary }}
        >
          Robomotion RPA · Automation · SaaS
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.05] mb-4"
        >
          Automation that<br />
          pays for itself.
        </motion.h1>
        <div className="h-12 mb-8 overflow-hidden relative" style={{ color: primary }}>
          {phrases.map((p, i) => (
            <motion.p
              key={i}
              className="text-2xl sm:text-3xl font-bold absolute"
              animate={{ y: (i - typedIdx) * 48, opacity: i === typedIdx ? 1 : 0 }}
              transition={{ duration: 0.4 }}
            >
              {p}
            </motion.p>
          ))}
        </div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-base sm:text-lg max-w-2xl mb-10"
          style={{ color: '#5C6B72', lineHeight: 1.6 }}
        >
          I'm Krris — I build bots, workflows, and SaaS that do the work you've been doing by hand.
          My practice runs on <span className="font-bold" style={{ color: '#1A2428' }}>Robomotion RPA</span>,
          and I ship n8n, Make, Zapier, and custom FastAPI + React apps.
          Teams hire me when spreadsheets stop scaling.
        </motion.p>
        <div className="flex flex-wrap gap-3">
          <Link to="/signup" className="inline-flex items-center gap-2 px-6 py-3 rounded-btn font-bold text-white text-sm shadow-lg" style={{ background: primary }} data-testid="hero-cta">
            Start your project <ArrowRight size={14} />
          </Link>
          <Link to="/audit" className="inline-flex items-center gap-2 px-6 py-3 rounded-btn font-bold text-sm" style={{ background: '#D9A44115', border: `1px solid ${primary}`, color: primary }} data-testid="audit-cta">
            <Sparkles size={14} /> Get a free audit
          </Link>
          <a href="#use-cases" className="inline-flex items-center gap-2 px-6 py-3 rounded-btn font-bold text-sm" style={{ background: 'white', border: '1px solid #E8E3D9', color: '#1A2428' }}>
            See real ROI <TrendingUp size={14} />
          </a>
        </div>

        <div className="mt-12 grid grid-cols-3 gap-4 max-w-xl">
          {[
            { label: 'Bots deployed', value: '47+' },
            { label: 'Hours reclaimed', value: '12K/mo' },
            { label: 'Avg ROI', value: '8.2×' },
          ].map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + i * 0.1 }}
            >
              <p className="text-3xl font-black" style={{ color: primary }}>{s.value}</p>
              <p className="text-xs uppercase tracking-wider font-semibold" style={{ color: '#5C6B72' }}>{s.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="relative z-10 max-w-6xl mx-auto px-6 py-20">
        <p className="text-xs uppercase tracking-[0.3em] font-bold mb-3" style={{ color: primary }}>Services</p>
        <h2 className="text-4xl sm:text-5xl font-black mb-3">Pick your flavor.</h2>
        <p className="text-base mb-12" style={{ color: '#5C6B72' }}>Robomotion RPA is where I go deep. The rest of the stack is here when you need it.</p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                whileHover={{ y: -4 }}
                className={`p-6 rounded-card bg-white border relative overflow-hidden ${s.hero ? 'lg:col-span-2 lg:row-span-2' : ''}`}
                style={{
                  borderColor: s.hero ? primary : '#E8E3D9',
                  boxShadow: s.hero ? `0 8px 32px ${primary}25` : 'none',
                }}
                data-testid={`service-${s.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {s.hero && (
                  <div className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full" style={{ background: primary, color: 'white' }}>
                    Core practice
                  </div>
                )}
                <div className="w-12 h-12 rounded-btn flex items-center justify-center mb-4" style={{ background: s.hero ? primary : `${primary}15` }}>
                  <Icon size={s.hero ? 22 : 18} style={{ color: s.hero ? 'white' : primary }} />
                </div>
                <h3 className={`font-black mb-1 ${s.hero ? 'text-3xl' : 'text-lg'}`}>{s.title}</h3>
                <p className="text-xs uppercase tracking-wider font-semibold mb-4" style={{ color: primary }}>{s.tagline}</p>
                <ul className="space-y-1.5">
                  {s.points.map(p => (
                    <li key={p} className="text-sm flex gap-2 items-start" style={{ color: '#5C6B72' }}>
                      <CheckCircle2 size={12} className="mt-0.5 flex-shrink-0" style={{ color: primary }} /> {p}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* USE CASES */}
      <section id="use-cases" className="relative z-10 max-w-6xl mx-auto px-6 py-20">
        <p className="text-xs uppercase tracking-[0.3em] font-bold mb-3" style={{ color: primary }}>Real ROI</p>
        <h2 className="text-4xl sm:text-5xl font-black mb-3">What changes after automation.</h2>
        <p className="text-base mb-10" style={{ color: '#5C6B72' }}>Numbers from production deployments this year.</p>

        <div className="grid md:grid-cols-2 gap-4">
          {USE_CASES.map((u, i) => (
            <motion.div
              key={u.title}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="p-6 rounded-card bg-white border flex gap-4 items-start"
              style={{ borderColor: '#E8E3D9' }}
              data-testid={`usecase-${i}`}
            >
              <div className="flex-shrink-0">
                <p className="text-3xl font-black leading-tight" style={{ color: primary }}>{u.metric}</p>
              </div>
              <div>
                <h3 className="font-black text-lg mb-1">{u.title}</h3>
                <p className="text-sm" style={{ color: '#5C6B72' }}>{u.detail}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* VALUE / ROI */}
      <section className="relative z-10 py-24" style={{ background: '#3D1B4E', color: 'white' }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] font-bold mb-3" style={{ color: '#F5C651' }}>Value for money</p>
              <h2 className="text-4xl sm:text-5xl font-black mb-4 leading-tight" style={{ color: '#FFFFFF' }}>
                One bot. One month.<br />
                <span style={{ color: '#F5C651' }}>Paid back forever.</span>
              </h2>
              <p className="text-base mb-6" style={{ color: '#E2D4EC', lineHeight: 1.7 }}>
                Hiring a person for a repetitive task runs $4-8K/month. A Robomotion bot
                doing the same thing: a one-time build fee + $50/month infra.
                The bot doesn't quit, doesn't forget, doesn't call in sick.
              </p>
              <div className="space-y-2">
                {['Transparent fixed-price pilots', 'Source code + docs yours to keep', 'Managed ops available if you want them', 'No per-user SaaS markup'].map(t => (
                  <div key={t} className="flex items-center gap-2 text-sm" style={{ color: '#F5EDFA' }}>
                    <CheckCircle2 size={14} style={{ color: '#F5C651' }} /> {t}
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="p-6 rounded-card" style={{ background: '#2A0F3D', border: '1px solid #5A2C6E' }}>
                <p className="text-xs uppercase tracking-wider font-bold mb-4" style={{ color: '#C9A8DE' }}>Typical pilot ROI</p>
                <div className="space-y-4">
                  <div className="flex justify-between items-baseline" style={{ color: '#FFFFFF' }}>
                    <span className="text-sm" style={{ color: '#E2D4EC' }}>Pilot build</span>
                    <span className="font-mono font-bold">$3-8K</span>
                  </div>
                  <div className="flex justify-between items-baseline" style={{ color: '#FFFFFF' }}>
                    <span className="text-sm" style={{ color: '#E2D4EC' }}>Monthly infra</span>
                    <span className="font-mono font-bold">$20-150</span>
                  </div>
                  <div className="flex justify-between items-baseline" style={{ color: '#FFFFFF' }}>
                    <span className="text-sm" style={{ color: '#E2D4EC' }}>Hours reclaimed / month</span>
                    <span className="font-mono font-bold">20-160h</span>
                  </div>
                  <div className="border-t pt-4 flex justify-between items-baseline" style={{ borderColor: '#5A2C6E', color: '#FFFFFF' }}>
                    <span className="text-sm font-bold">Break-even</span>
                    <span className="font-mono font-black text-xl" style={{ color: '#F5C651' }}>&lt; 90 days</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT / CTA */}
      <section id="contact" className="relative z-10 max-w-4xl mx-auto px-6 py-24 text-center">
        <Shield size={32} style={{ color: primary, margin: '0 auto 20px' }} />
        <h2 className="text-4xl sm:text-5xl font-black mb-4">Ready when you are.</h2>
        <p className="text-base max-w-xl mx-auto mb-10" style={{ color: '#5C6B72' }}>
          Tell me about the process that's eating your team's week.
          I'll reply within 7 days with a scoped pilot proposal.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/signup" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-btn font-bold text-white text-sm shadow-lg" style={{ background: primary }} data-testid="contact-signup">
            Start your project <ArrowRight size={14} />
          </Link>
          <a href="mailto:hello@krri.sh" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-btn font-bold text-sm" style={{ background: 'white', border: '1px solid #E8E3D9', color: '#1A2428' }} data-testid="contact-email">
            <Mail size={14} /> hello@krri.sh
          </a>
        </div>
      </section>

      <footer className="relative z-10 border-t py-8 text-center text-xs" style={{ borderColor: '#E8E3D9', color: '#8A99A0' }}>
        © {new Date().getFullYear()} krri.sh · Built on <Link to="/features" className="font-semibold" style={{ color: primary }}>{branding.app_name}</Link>
      </footer>
    </div>
  );
}
